import { Order } from "../../../core/models/entities/Order";
import { Role } from "../../../core/models/entities/Role";
import { enviroment } from "../../../enviroment";
import { agreedPriceApplies } from "./subcomponents/OrderLineData";

// Admin price adjustment (flag REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED):
// pure display rules shared by the order list row, the read-only order
// confirmation view and the order details. No data calls anywhere here -
// everything is derived from the order payload already on screen.

// Query parameter that opens the "Price adjustments" panel on the order
// details right away (the list badge and the confirmation notice link there).
export const PRICE_ADJUSTMENTS_PARAM = "priceAdjustments";

export function priceAdjustmentsDetailsUrl(order: Order): string {
  return (
    `/order/details?orderId=${order.id}&orgId=${order.organization.id}` +
    `&${PRICE_ADJUSTMENTS_PARAM}=1`
  );
}

// A line carrying an agreed unit price. The API's convention mapping puts
// agreedUnitPrice on every OrderDto read (list rows, org-scoped and admin
// reads) while its flag is on, so this is answerable without the admin read.
export function orderHasAgreedPrices(order: Order): boolean {
  return (order.lines ?? []).some(
    (line) =>
      line.product?.agreedUnitPrice !== undefined &&
      line.product?.agreedUnitPrice !== null,
  );
}

// "Was adjusted": the admin read says so (priceAdjustment summary), or the
// lines still carry an agreement. Known gap: an adjustment that removed
// every agreement again leaves the list row without a badge (the list
// payload has no summary) - the details banner still shows it.
export function orderIsPriceAdjusted(
  order: Order,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): boolean {
  if (!enabled) return false;
  return !!order.priceAdjustment || orderHasAgreedPrices(order);
}

export interface OrderTotals {
  subTotal: number;
  vat: number;
  total: number;
}

// The read-only order confirmation (/order/confirm, the admin's landing page
// from the order list) sums its totals client-side from the stored line
// amounts (VendorPrice/CustomerPrice x qty), which an adjustment never
// changes - so it kept showing the pre-adjustment totals no matter how
// fresh the fetch was. For a placed order whose lines carry an agreed price,
// read on the vendor basis (admin, or a vendor on its own prices - the same
// rule the rows use to strike our price through), the API's stored totals
// are the fact: they are what the new payment link charges. Returns
// undefined - "compute as before" - for drafts, for the customer-price view,
// for orders without any agreement, when the stored totals are missing,
// and always while the FE flag is off (byte-identical totals).
export function placedOrderTotalsOverride(
  order: Order,
  role: Role | undefined,
  showCustomerPrices: boolean,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): OrderTotals | undefined {
  if (!enabled) return undefined;
  if (!agreedPriceApplies(role, showCustomerPrices, enabled)) return undefined;
  if (!orderHasAgreedPrices(order)) return undefined;
  if (!order.subTotal || !order.total) return undefined;
  return {
    subTotal: order.subTotal,
    vat: order.vat ?? 0,
    total: order.total,
  };
}
